import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, UserX, PhoneOff, MailX } from 'lucide-react';

interface AttentionRequiredProps {
  staleLeads: number;
  neverContacted: number;
  staleNoPhone: number;
  staleNoEmail: number;
  staleDaysThreshold: number;
}

const AttentionRequired = ({
  staleLeads,
  neverContacted,
  staleNoPhone,
  staleNoEmail,
  staleDaysThreshold,
}: AttentionRequiredProps) => {
  const navigate = useNavigate();

  const items = [
    {
      icon: AlertTriangle,
      label: 'Stale leads',
      sublabel: `No activity in ${staleDaysThreshold}+ days`,
      count: staleLeads,
      color: 'text-destructive',
      bg: 'bg-destructive/10',
      cta: 'Work Stale',
      link: `/app/leads?stale_days=${staleDaysThreshold}`,
    },
    {
      icon: UserX,
      label: 'Never contacted',
      sublabel: 'No outreach recorded',
      count: neverContacted,
      color: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-500/10',
      cta: 'Start Contacting',
      link: '/app/leads?no_activity=true',
    },
    {
      icon: PhoneOff,
      label: 'Missing phone',
      sublabel: 'Stale leads without phone',
      count: staleNoPhone,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      cta: 'View',
      link: `/app/leads?stale_days=${staleDaysThreshold}&has_phone=false`,
    },
    {
      icon: MailX,
      label: 'Missing email',
      sublabel: 'Stale leads without email',
      count: staleNoEmail,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      cta: 'View',
      link: `/app/leads?stale_days=${staleDaysThreshold}&has_email=false`,
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          Attention Required
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => (
          <div
            key={item.label}
            className={`flex items-center gap-3 rounded-lg p-2.5 cursor-pointer hover:opacity-80 transition-opacity ${item.bg}`}
            onClick={() => navigate(item.link)}
          >
            <item.icon className={`h-4 w-4 shrink-0 ${item.color}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight">{item.label}</p>
              <p className="text-xs text-muted-foreground truncate">
                {item.sublabel}
              </p>
            </div>
            <span className={`text-lg font-bold shrink-0 ${item.color}`}>
              {item.count}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground font-medium">
              {item.cta} →
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default AttentionRequired;
